import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging'
import { create } from 'zustand'
import { app, db, usingEmulators } from './firebase'
import { showToast } from '@/store/ui'

// Push on this device. Permission is requested only from the Settings button,
// never on load. The token is saved at users/{uid}/devices/{deviceId},
// refreshed when the app opens, and deleted on sign-out. One service worker
// handles both the app shell and push; its registration is passed to getToken.

interface PushState {
  supported: boolean | null
  permission: NotificationPermission | 'unsupported'
  /** This device has a saved token. */
  enabled: boolean
  busy: boolean
  error: string | null
}

export const usePush = create<PushState>()(() => ({
  supported: null,
  permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  enabled: false,
  busy: false,
  error: null,
}))

const DEVICE_KEY = 'cuplzap.deviceId'
const ENABLED_KEY = 'cuplzap.pushEnabled'

/** A stable id for this browser, so the token document is replaced rather than duplicated. */
export function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY)
    if (!id) {
      id = crypto.randomUUID().replace(/-/g, '').slice(0, 20)
      localStorage.setItem(DEVICE_KEY, id)
    }
    return id
  } catch {
    return 'device'
  }
}

let registration: ServiceWorkerRegistration | null = null
let messaging: Messaging | null = null

export function setPushRegistration(reg: ServiceWorkerRegistration): void {
  registration = reg
}

async function messagingFor(): Promise<Messaging | null> {
  if (messaging) return messaging
  if (!(await isSupported())) return null
  messaging = getMessaging(app)
  return messaging
}

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || ''

/** Under the emulators with no push key there is no FCM: a stand-in token lets the rest of the flow run. */
export const mockPush = usingEmulators && !vapidKey

/** Saves the current token for this device; called from the button and on each open when enabled. */
async function saveToken(uid: string): Promise<boolean> {
  if (mockPush) {
    // no FCM in the emulators: a stand-in token lets the rest of the flow run
    await setDoc(doc(db, 'users', uid, 'devices', deviceId()), { token: `mock-${deviceId()}`, userAgent: navigator.userAgent.slice(0, 200), updatedAt: serverTimestamp() })
    return true
  }
  const m = await messagingFor()
  if (!m || !registration) return false
  const token = await getToken(m, { vapidKey, serviceWorkerRegistration: registration })
  if (!token) return false
  await setDoc(doc(db, 'users', uid, 'devices', deviceId()), { token, userAgent: navigator.userAgent.slice(0, 200), updatedAt: serverTimestamp() })
  return true
}

export function pushAvailable(): boolean {
  return typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
}

/** "Turn on notifications on this device": asks permission on this tap only, then saves the token. */
export async function enablePush(uid: string): Promise<void> {
  usePush.setState({ busy: true, error: null })
  try {
    if (!mockPush) {
      if (!pushAvailable()) throw new Error('This browser cannot show notifications.')
      const permission = await Notification.requestPermission()
      usePush.setState({ permission })
      if (permission !== 'granted') throw new Error('Notifications were not allowed. Check the browser or system settings.')
    }
    const ok = await saveToken(uid)
    if (!ok) throw new Error('Could not get a push token for this device.')
    try {
      localStorage.setItem(ENABLED_KEY, '1')
    } catch {
      // storage blocked: the flag is re-derived from the token on the next open
    }
    usePush.setState({ enabled: true })
  } catch (err) {
    usePush.setState({ error: (err as Error).message })
    throw err
  } finally {
    usePush.setState({ busy: false })
  }
}

/** On each open: refresh the token when this device is enabled, and listen for foreground messages. */
export async function startPush(uid: string): Promise<() => void> {
  let enabled = false
  try {
    enabled = localStorage.getItem(ENABLED_KEY) === '1'
  } catch {
    enabled = false
  }
  usePush.setState({ supported: pushAvailable() || mockPush, enabled })
  if (enabled && (mockPush || (typeof Notification !== 'undefined' && Notification.permission === 'granted'))) {
    saveToken(uid).catch((err: unknown) => console.warn('push token refresh', err))
  }
  const m = await messagingFor().catch(() => null)
  if (!m) return () => undefined
  // when the app is open and visible, show an in-app toast instead of a system notification
  return onMessage(m, (payload) => {
    const title = payload.notification?.title ?? payload.data?.title ?? ''
    const body = payload.notification?.body ?? payload.data?.body ?? ''
    showToast([title, body].filter(Boolean).join(' · '), null, 8000)
  })
}

/** On sign-out: delete this device's token so no push reaches a signed-out device. */
export async function disablePush(uid: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'users', uid, 'devices', deviceId()))
  } catch {
    // offline or already gone
  }
  try {
    localStorage.removeItem(ENABLED_KEY)
  } catch {
    // storage blocked
  }
  usePush.setState({ enabled: false })
}
